// ============================================================
// TMR Portal Admin — an inline message, in one of four tones
// ============================================================
import type { ReactNode } from 'react';

/** Props for {@link Notice}. */
export interface NoticeProps {
  /** How loudly to say it. `danger` also announces itself to assistive technology. */
  tone: 'neutral' | 'success' | 'warning' | 'danger';
  children: ReactNode;
}

/**
 * A message shown next to the thing it is about.
 *
 * Inline rather than a toast because the portal has no toast infrastructure (09, lacuna L12) — and for these
 * messages inline is the better answer anyway: they explain the state of a form or a call the operator is in
 * the middle of, and a notification that slides away takes the explanation with it.
 *
 * It lives here rather than beside a screen because it has more than one consumer: the creation flow and the
 * send confirmation both need it, and a second copy would drift in tone the first time one was adjusted.
 */
export function Notice({ tone, children }: NoticeProps) {
  const tones = {
    neutral: { bg: 'var(--cream-100)', border: 'var(--border-default)', fg: 'var(--fg-muted)' },
    success: { bg: 'oklch(96% 0.03 155)', border: 'oklch(82% 0.08 155)', fg: 'oklch(34% 0.08 155)' },
    warning: { bg: 'oklch(96% 0.04 80)', border: 'oklch(84% 0.09 80)', fg: 'oklch(38% 0.10 60)' },
    danger: { bg: 'oklch(96% 0.03 28)', border: 'oklch(80% 0.09 28)', fg: 'oklch(38% 0.12 28)' },
  }[tone];

  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      style={{
        marginTop: 14,
        padding: '12px 14px',
        background: tones.bg,
        border: `1px solid ${tones.border}`,
        borderRadius: 'var(--radius-sm)',
        fontSize: 13,
        lineHeight: 1.55,
        color: tones.fg,
      }}
    >
      {children}
    </div>
  );
}
